/**
 * Sentry error reporting — frontend.
 *
 * Phase 1 (runtime SDK): captures unhandled errors, promise rejections, and
 * React render errors (via the ErrorBoundary hook) from real deployments.
 * Source-map upload (readable stack traces) is a separate build-time step.
 *
 * Disabled in offline dev / dev-bypass and whenever no DSN is configured —
 * mirrors the App Check gate so local development never phones home
 * (docs/lessons.md Lesson #23 / Pitfall — `hasRealFirebaseConfig`).
 *
 * PII discipline: WSS handles customer mobiles, emails, and payment figures,
 * and Sentry is a third party. `sendDefaultPii` is off and `beforeSend` /
 * `beforeBreadcrumb` redact PII-looking keys from the structured payload
 * before anything leaves the browser.
 */

import * as Sentry from '@sentry/react';
import { env, hasRealFirebaseConfig } from './env';
import { shouldDropEvent } from './sentryFilters';

/** Set once `main.tsx` commits to a stale-chunk recovery reload. Errors raised
 *  after that point are teardown casualties of a navigation already underway —
 *  see `sentryFilters.ts` for why React.lazy throws there at all. */
let chunkReloadInFlight = false;

/** Called by the `vite:preloadError` handler immediately before it reloads. */
export function markChunkReloadInFlight(): void {
  chunkReloadInFlight = true;
}

/** Keys whose values must never leave the browser. Matched case-insensitively
 *  against object keys in event extra / contexts / breadcrumb data. */
const PII_KEY_PATTERN = /mobile|phone|email|amount|grand_?total|ledger|nric|card|iban|account/i;

const REDACTED = '[redacted]';

/** Recursively redact PII-looking keys from an arbitrary value. Depth-bounded
 *  so a pathological / cyclic object can't hang the reporter. */
function redact(value: unknown, depth = 0): unknown {
  if (depth > 6 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = PII_KEY_PATTERN.test(k) ? REDACTED : redact(v, depth + 1);
  }
  return out;
}

export function initSentry(): void {
  // No real backend (offline dev / dev-bypass) or no DSN → reporting disabled.
  if (!hasRealFirebaseConfig() || !env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: env.VITE_SENTRY_RELEASE || undefined,
    // Errors only to start: no performance tracing (noise/cost) and no session
    // replay (it records the DOM, i.e. customer data). Revisit replay later
    // with aggressive masking once the error feed is trusted.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      // Known non-defects (stale-chunk reload fallout, IndexedDB refusals) are
      // dropped before anything else — returning null suppresses the event.
      if (shouldDropEvent(event, { chunkReloadInFlight })) return null;
      // Drop request cookies / headers wholesale, then redact PII-looking keys
      // from the structured payload.
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
      }
      if (event.extra) event.extra = redact(event.extra) as typeof event.extra;
      if (event.contexts) event.contexts = redact(event.contexts) as typeof event.contexts;
      return event;
    },
    beforeBreadcrumb(crumb) {
      if (crumb.data) crumb.data = redact(crumb.data) as typeof crumb.data;
      return crumb;
    },
  });
}

/**
 * Attach the signed-in identity to subsequent events, so an issue's "users
 * impacted" is a real number — without it every issue reads 0 and forty events
 * could be one person or ten.
 *
 * The Firebase **uid** only. Never the email: it is PII, it is what
 * `PII_KEY_PATTERN` strips from every other part of the payload, and the uid
 * already resolves to a person in the Firebase console when triage needs it.
 * The role rides along as a tag because it is the useful filter (a fault that
 * only MEASURER hits is a different fault).
 *
 * No-op before `initSentry` / when reporting is disabled.
 */
export function setSentryUser(uid: string | null, role: string | null): void {
  if (!uid) {
    Sentry.setUser(null);
    Sentry.setTag('role', undefined);
    return;
  }
  Sentry.setUser({ id: uid });
  Sentry.setTag('role', role ?? 'unknown');
}

/**
 * Capture an exception. A no-op when Sentry was never initialised (offline dev
 * / no DSN), so callers don't need to guard. Centralising the SDK import here
 * keeps `@sentry/react` out of feature code.
 */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
